<template>
    <div class="">
        <Label for="email">{{ $t("workflows.email.content") }}</Label>
        <div
            class="mt-2 rounded-t-md border-iron-300 bg-iron-100 border-t border-r border-l"
        >
            <button
                type="button"
                :class="[
                    editor.isActive('bold') ? 'bg-white' : '',
                    'rounded-t-md p-2',
                ]"
                @click="editor.chain().focus().toggleBold().run()"
            >
                <Bold class="text-iron-600" :size="16" stroke-width="2.2" />
            </button>
            <button
                type="button"
                :class="[editor.isActive('italic') ? 'bg-white' : '', 'p-2']"
                @click="editor.chain().focus().toggleItalic().run()"
            >
                <Italic class="text-iron-600" :size="16" stroke-width="2.2" />
            </button>
            <button
                type="button"
                :class="[editor.isActive('underline') ? 'bg-white' : '', 'p-2']"
                @click="editor.chain().focus().toggleUnderline().run()"
            >
                <UnderlineIcon
                    class="text-iron-600"
                    :size="16"
                    stroke-width="2.2"
                />
            </button>
            <DropdownMenu>
                <DropdownMenuTrigger>
                    <button type="button" class="p-2">
                        <Braces
                            class="text-iron-600"
                            :size="16"
                            stroke-width="2.2"
                        />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent class="ml-12">
                    <ScrollArea class="h-64">
                        <DropdownMenuLabel>{{
                            $t("workflows.email.default_merge_tags")
                        }}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            v-for="tag in mergeTags"
                            :key="tag"
                            @click="insertContent(tag)"
                        >
                            {{
                                $t(
                                    `workflows.email.merge_tags.${tag.toLowerCase()}`,
                                )
                            }}
                            <span class="ml-2 tracking-tighter text-iron-500">
                                {{ "{" + tag + "}" }}
                            </span>
                        </DropdownMenuItem>
                    </ScrollArea>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
        <EditorContent :editor="editor"></EditorContent>
    </div>
</template>

<script lang="ts" setup>
import { Editor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import { onBeforeUnmount } from "vue";
import {
    Bold,
    Braces,
    Italic,
    Underline as UnderlineIcon,
} from "lucide-vue-next";
import Underline from "@tiptap/extension-underline";
import Mention from "@tiptap/extension-mention";
import suggestion from "./suggestion";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import ScrollArea from "@/components/ui/scroll-area/ScrollArea.vue";

const model = defineModel<string>();

const emit = defineEmits(["text-changed"]);

const mergeTags = [
    "FIRST_NAME",
    "LAST_NAME",
    "EMAIL",
    "PHONE",
    "DATE",
    "TIMEZONE",
    "LOCATION_LABEL",
    "LOCATION_VALUE",
    "EVENT_NAME",
    "DURATION",
    "CANCEL_LINK",
    "VOCATIVE_FIRST_NAME",
];

const editor = new Editor({
    content: `${model.value}`,
    extensions: [
        StarterKit,
        Underline,
        Mention.configure({
            HTMLAttributes: {
                class: "merge-tag",
                "data-type": "merge-tag",
            },
            renderText({ options, node }) {
                return `{${node.attrs.label ?? node.attrs.id}}`;
            },

            renderHTML({ options, node }) {
                return [
                    "span",
                    options.HTMLAttributes,
                    `${node.attrs.label ?? node.attrs.id}`,
                ];
            },
            suggestion,
        }),
    ],
    onUpdate({ editor }) {
        model.value = editor.getHTML();
        emit("text-changed", editor.getText());
    },
    editorProps: {
        handleKeyDown(view, event) {
            if (event.key !== "Enter" || event.shiftKey) {
                return false;
            }

            const hardBreak = view.state.schema.nodes.hardBreak;

            if (!hardBreak) {
                return false;
            }

            event.preventDefault();
            view.dispatch(
                view.state.tr
                    .replaceSelectionWith(hardBreak.create())
                    .scrollIntoView(),
            );

            return true;
        },
        attributes: {
            class: "min-h-[240px] max-w-full w-full focus:outline-none rounded-b-lg border border-iron-300 focus:border-z-primary-300 px-3 py-2 text-sm text-iron-900 shadow-xs ring-purple-100 focus:outline-none focus:ring-2",
        },
    },
});

function insertContent(content: string) {
    editor
        .chain()
        .focus()
        .insertContent({
            type: "mention",
            attrs: {
                id: content,
            },
        })
        .insertContent(" ") // add an extra space after the mention
        .run();
}

onBeforeUnmount(() => {
    editor.destroy();
});
</script>

<style>
.tiptap:first-child {
    margin-top: 0;
}

.tiptap .merge-tag {
    background-color: #5805ff0d;
    border-radius: 0.4rem;
    box-decoration-break: clone;
    color: #6a00f5;
    padding: 0.1rem 0.3rem;
}
</style>
